import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, platformLabel } from "../../lib/api";
import { formatDateDisplay } from "../../lib/utils";
import { toast } from "sonner";
import { CalendarDays, ExternalLink, Pencil, Plus, Trash2, Upload, X } from "lucide-react";

const EMPTY_FORM = {
  name: "",
  slug: "",
  banner_image: "",
  starts_at: "",
  ends_at: "",
  product_ids: [],
  active: true,
};

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

export default function AdminSales() {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bannerUrlDraft, setBannerUrlDraft] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const load = async () => {
    const r = await api.get("/admin/sales");
    setSales(r.data);
  };

  useEffect(() => {
    load();
    api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data));
  }, []);

  const selectedProducts = useMemo(
    () => form.product_ids.map((id) => products.find((p) => p.id === id)).filter(Boolean),
    [form.product_ids, products]
  );

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return products
      .filter((p) => !q || `${p.name} ${p.platform} ${p.category}`.toLowerCase().includes(q))
      .slice(0, 50);
  }, [productSearch, products]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setProductSearch("");
    setShowForm(true);
  };

  const openEdit = (sale) => {
    setEditingId(sale.id);
    setForm({
      name: sale.name || "",
      slug: sale.slug || "",
      banner_image: sale.banner_image || "",
      starts_at: sale.starts_at || "",
      ends_at: sale.ends_at || "",
      product_ids: sale.product_ids || [],
      active: sale.active,
    });
    setProductSearch("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setProductSearch("");
  };

  const setName = (name) => {
    setForm((f) => ({ ...f, name, slug: editingId ? f.slug : slugify(name) }));
  };

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id)
        ? f.product_ids.filter((x) => x !== id)
        : [...f.product_ids, id],
    }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, banner_image: r.data.url }));
      toast.success("Banner uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.name),
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      };
      if (editingId) {
        await api.patch(`/admin/sales/${editingId}`, payload);
        toast.success("Sale updated");
      } else {
        await api.post("/admin/sales", payload);
        toast.success("Sale created");
      }
      closeForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save sale");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (sale) => {
    if (!window.confirm(`Delete sale "${sale.name}"?`)) return;
    await api.delete(`/admin/sales/${sale.id}`);
    toast.success("Sale deleted");
    await load();
  };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Promotions</div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-black uppercase tracking-tighter">Sales</h1>
        <button onClick={openCreate} className="flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full hover:bg-gold-light btn-glow transition-all">
          <Plus className="w-3.5 h-3.5" /> New Sale
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-navy-700 rounded-xl neon-border p-6 mb-8 space-y-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gold">
            {editingId ? "Edit Sale" : "Create Sale"}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Sale Name" value={form.name} onChange={(e) => setName(e.target.value)} placeholder="Black Friday" required />
            <FormField label="URL Slug" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))} placeholder="black-friday" required />
            <DateInput label="Starts At" value={form.starts_at || ""} onChange={(v) => setForm((f) => ({ ...f, starts_at: v }))} />
            <DateInput label="Ends At" value={form.ends_at || ""} onChange={(v) => setForm((f) => ({ ...f, ends_at: v }))} />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Thin Banner Image</div>
            {form.banner_image && (
              <div className="relative h-28 rounded-lg overflow-hidden border border-gold/20 mb-3 bg-navy-900">
                <img src={form.banner_image} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setForm((f) => ({ ...f, banner_image: "" }))} className="absolute top-2 right-2 bg-rose-500 text-white rounded-full p-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="grid sm:grid-cols-[1fr_1fr] gap-2">
              <label className="cursor-pointer flex items-center gap-2 bg-navy-800 border border-gold/20 rounded-md px-3 py-2 text-xs hover:border-gold/50">
                <Upload className="w-4 h-4 text-gold" /> {uploading ? "Uploading..." : "Upload banner"}
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
              </label>
              <input
                value={bannerUrlDraft}
                onChange={(e) => setBannerUrlDraft(e.target.value)}
                disabled={uploading}
                placeholder="or paste banner URL + Enter"
                className="w-full bg-navy-800 border border-gold/20 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-gold/60 disabled:opacity-50"
                onKeyDown={async (e) => {
                  const val = bannerUrlDraft.trim();
                  if (e.key !== "Enter" || !val) return;
                  setUploading(true);
                  try {
                    const r = await api.post("/admin/upload-from-url", { url: val });
                    setForm((f) => ({ ...f, banner_image: r.data.url }));
                    setBannerUrlDraft("");
                    toast.success("Banner saved to CDN");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not fetch that URL");
                  } finally {
                    setUploading(false);
                  }
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Products in Sale</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{form.product_ids.length} selected</div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="accent-gold" />
                Active
              </label>
            </div>

            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedProducts.map((p) => (
                  <span key={p.id} className="flex items-center gap-1 bg-gold/20 text-gold text-[11px] font-semibold px-2 py-1 rounded-full">
                    {p.name}
                    <button type="button" onClick={() => toggleProduct(p.id)} className="hover:text-rose-400 transition">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products..." className="w-full bg-navy-800 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
            <div className="max-h-64 overflow-y-auto border border-gold/10 rounded-lg divide-y divide-gold/10">
              {filteredProducts.map((p) => {
                const selected = form.product_ids.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => toggleProduct(p.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition ${selected ? "bg-gold/10 text-gold" : "hover:bg-navy-700/60 text-slate-300"}`}>
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-9 h-9 object-cover rounded shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-500">{platformLabel(p.platform)} / {p.category} / Rs {Number(p.price || 0).toLocaleString()}</div>
                    </div>
                    {selected && <span className="text-[10px] font-black text-gold">Selected</span>}
                  </button>
                );
              })}
              {filteredProducts.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">No products match.</div>}
            </div>
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={saving || !form.name || form.product_ids.length === 0} className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-6 py-2.5 rounded-full hover:bg-gold-light transition-all disabled:opacity-50">
              {saving ? "Saving..." : "Save Sale"}
            </button>
            <button type="button" onClick={closeForm} className="text-slate-400 hover:text-white text-xs uppercase tracking-wider px-4 py-2.5">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-navy-700 rounded-xl neon-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-navy-800 text-[10px] uppercase tracking-[0.2em] text-gold/80">
            <tr>
              <th className="text-left p-4">Sale</th>
              <th className="text-left p-4">Timeline</th>
              <th className="text-left p-4">Products</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sales.map((sale) => (
              <tr key={sale.id} className="border-t border-gold/10">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {sale.banner_image && <img src={sale.banner_image} alt="" className="w-20 h-10 object-cover rounded bg-navy-900" />}
                    <div className="min-w-0">
                      <div className="font-bold truncate">{sale.name}</div>
                      <Link to={`/sale/${sale.slug}`} target="_blank" className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-gold transition">
                        /sale/{sale.slug} <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-xs text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-gold" />
                    {formatDate(sale.starts_at) || "Now"} to {formatDate(sale.ends_at) || "No end"}
                  </div>
                </td>
                <td className="p-4 font-bold">{sale.product_ids?.length || 0}</td>
                <td className="p-4">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${sale.live ? "bg-emerald-500/20 text-emerald-300" : sale.active ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}`}>
                    {sale.live ? "Live" : sale.active ? "Scheduled" : "Inactive"}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(sale)} title="Edit" className="p-1.5 text-slate-400 hover:text-gold transition">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(sale)} title="Delete" className="p-1.5 text-slate-400 hover:text-rose-400 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sales.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">No sales yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormField({ label, ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <input {...rest} className="w-full mt-1 bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60" />
    </label>
  );
}

// value/onChange use YYYY-MM-DD; displays and accepts DD-MM-YYYY
function DateInput({ label, value, onChange }) {
  const toDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (y && m && d) return `${d}-${m}-${y}`;
    return iso;
  };
  const [display, setDisplay] = React.useState(() => toDisplay(value));
  React.useEffect(() => { setDisplay(toDisplay(value)); }, [value]);

  const handle = (e) => {
    let raw = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    let formatted = raw;
    if (raw.length > 4) formatted = raw.slice(0, 2) + "-" + raw.slice(2, 4) + "-" + raw.slice(4);
    else if (raw.length > 2) formatted = raw.slice(0, 2) + "-" + raw.slice(2);
    setDisplay(formatted);
    if (raw.length === 8) {
      const iso = `${raw.slice(4)}-${raw.slice(2, 4)}-${raw.slice(0, 2)}`;
      onChange(iso);
    } else {
      onChange("");
    }
  };

  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <input
        value={display}
        onChange={handle}
        placeholder="DD-MM-YYYY"
        className="w-full mt-1 bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60"
      />
    </label>
  );
}

function formatDate(value) {
  return formatDateDisplay(value);
}
